/**
 * @mentions in chat (migration 084): finding "@Ri" while it is typed, the
 * people it could be, which names a message mentions, and where in its text
 * they are - so they can be drawn as chips.
 *
 * A mention is written as plain text, "@Rita Gatt", and KEPT structurally by
 * the server as [{ key, name }] beside it. The words are what everybody reads
 * (and what an older page shows); the list is what says who was named. They
 * are matched the way the server matches them (backend lib/chat-branches
 * `says`): "@" + the whole name, case aside, not followed by more letters.
 */

const WORD = /[\p{L}\p{N}_]/u;

/**
 * The "@..." being typed just before the caret, or null: an "@" at the start
 * or after something that is not a letter, then up to 30 characters with no
 * new line - names have spaces in them, so a space does not end it.
 */
export const mentionAt = (text, caret) => {
  const s = String(text || '');
  const end = typeof caret === 'number' ? caret : s.length;
  const from = Math.max(0, end - 31);
  const at = s.lastIndexOf('@', end - 1);
  if (at < from || at < 0) return null;
  if (at > 0 && WORD.test(s[at - 1])) return null;
  const query = s.slice(at + 1, end);
  if (/[\n\r]/.test(query) || /^\s/.test(query) || /\s{2}/.test(query)) return null;
  return { start: at, query };
};

/** The people a "@query" could mean: names starting with it, or with a word starting with it. */
export const matchPeople = (people, query, limit = 6) => {
  const q = String(query || '').trim().toLowerCase();
  const list = Array.isArray(people) ? people : [];
  if (!q) return list.slice(0, limit);
  const starts = [];
  const words = [];
  for (const p of list) {
    const name = String(p.name || '').toLowerCase();
    if (name.startsWith(q)) starts.push(p);
    else if (name.split(/\s+/).some((w) => w.startsWith(q)) || name.includes(` ${q}`)) words.push(p);
  }
  return [...starts, ...words].slice(0, limit);
};

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const nameRe = (name, flags = 'iu') => new RegExp(`(^|[^\\p{L}\\p{N}_])@${escapeRe(name)}(?![\\p{L}\\p{N}_])`, flags);

/** Which of `people` the text names - their keys, for sending. */
export const mentionsIn = (body, people) => (Array.isArray(people) ? people : [])
  .filter((p) => p && p.name && nameRe(p.name).test(String(body || '')))
  .map((p) => p.key);

/**
 * The text cut into plain strings and mentions: [{ text }, { mention, text }].
 * Longest names first, so "@Rita Gatt" is not taken for "@Rita".
 */
export const splitMentions = (body, mentions) => {
  const s = String(body ?? '');
  const list = (Array.isArray(mentions) ? mentions : []).filter((m) => m && m.name)
    .sort((a, b) => b.name.length - a.name.length);
  if (!list.length) return [{ text: s }];
  const hits = [];
  for (const m of list) {
    const re = nameRe(m.name, 'giu');
    let x;
    // eslint-disable-next-line no-cond-assign
    while ((x = re.exec(s)) !== null) {
      const start = x.index + x[1].length;
      const end = start + 1 + m.name.length;
      if (!hits.some((h) => start < h.end && end > h.start)) hits.push({ start, end, mention: m });
      if (re.lastIndex === x.index) re.lastIndex += 1;
    }
  }
  hits.sort((a, b) => a.start - b.start);
  const out = [];
  let at = 0;
  for (const h of hits) {
    if (h.start > at) out.push({ text: s.slice(at, h.start) });
    out.push({ mention: h.mention, text: s.slice(h.start, h.end) });
    at = h.end;
  }
  if (at < s.length) out.push({ text: s.slice(at) });
  return out;
};
